import { Invitee } from "@core/types";
import { IonIcon, IonSpinner } from "@ionic/react";
import { peopleOutline, personCircleOutline, sadOutline } from "ionicons/icons";
import React from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router";
import { CustomBottomSheet } from "../../../../components/UI/CustomBottomSheet";
import { useAuth } from "../../../../contexts/auth-context";
import useGetReferralStatsQuery from "../../../../providers/referrals/useGetReferralStatsQuery";
import { useConfig } from "../../../../providers/config";

const ReferralStatsBottomSheet = () => {
  const { user } = useAuth();
  const referralId = user?.id || "";

  const config = useConfig();
  const history = useHistory();
  const { t } = useTranslation();
  const { data: referralStats, isLoading } = useGetReferralStatsQuery(referralId);

  const isEmpty =
    referralStats?.totalPurchases === 0 &&
    referralStats?.totalPointsEarned === 0 &&
    referralStats?.invitees?.length === 0;

  const handleGoToInvite = () => {
    history.push("/invite-friend");
  };

  const renderPreview = () => (
    <div className="flex items-center justify-center rounded-xl h-[1.75rem] px-3 py-1  bg-gray-100">
      <IonIcon icon={peopleOutline} className="w-5 h-5 text-purple-500 mr-2" />
      <span className="text-sm font-normal text-gray-900">{t("Invited friends")}</span>
    </div>
  );

  return (
    <CustomBottomSheet
      showDefaultHeader={false}
      renderPreview={renderPreview}
      snapPoints={({ maxHeight }) => [maxHeight * 0.5, maxHeight * 0.75, maxHeight]}
    >
      {isLoading ? (
        <div className="flex justify-center items-center h-48">
          <IonSpinner />
        </div>
      ) : (
        <div className="p-4 text-gray-900 rounded-t-2xl h-full">
          {isEmpty ? (
            <div className="flex flex-col items-center justify-center gap-2 h-full text-center">
              <h2 className="text-lg font-semibold">{t("You have not invited anyone yet")}</h2>
              <p className="text-gray-600">{config.inviteFriendDesc}</p>
              <button className="bg-primary font-bold px-4 py-2 rounded-lg" onClick={handleGoToInvite}>
                {t("Invite a friend")}
              </button>
            </div>
          ) : (
            <>
              <div className="text-center mb-4">
                <h2 className="text-xl font-bold">{t("Invited friends")}</h2>
              </div>
              <div className="grid grid-cols-2 gap-4 mb-4">
                <div className="bg-gray-100 p-4 rounded-lg flex flex-col items-center justify-center">
                  {/* <IonIcon icon={cartOutline} className="w-8 h-8 text-purple-500 mb-2" /> */}
                  <span className="text-sm font-medium text-gray-600">{t("Total spent")}</span>
                  <span className="text-xl font-bold text-gray-900">{referralStats?.totalPurchases}₽</span>
                </div>
                <div className="bg-gray-100 p-4 rounded-lg flex flex-col items-center justify-center">
                  {/* <IonIcon icon={giftOutline} className="w-8 h-8 text-orange-500 mb-2" /> */}
                  <span className="text-sm font-medium text-gray-600">{t("Points")}</span>
                  <span className="text-xl font-bold text-gray-900">{referralStats?.totalPointsEarned}</span>
                </div>
              </div>
              <div>
                {referralStats?.invitees && referralStats.invitees.length > 0 ? (
                  referralStats.invitees.map((invitee: Invitee) => (
                    <div
                      key={invitee.id}
                      className="flex items-center justify-between p-3 my-2 bg-white rounded-lg shadow"
                    >
                      <div className="flex items-center">
                        <div className="w-10 h-10 bg-gray-200 rounded-full flex items-center justify-center mr-3">
                          <IonIcon icon={personCircleOutline} className="text-gray-500 w-8 h-8" />
                        </div>
                        <span className="text-gray-900">{invitee.phone}</span>
                      </div>
                      {config.isInviteePurchasesEnabled && (
                        <span className="text-gray-900 font-bold">{invitee.purchases}₽</span>
                      )}
                    </div>
                  ))
                ) : (
                  <div className="text-center text-gray-500 mt-8">{t("You have not invited anyone yet")}</div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </CustomBottomSheet>
  );
};

export default ReferralStatsBottomSheet;
